import {match} from 'ts-pattern';

import {always} from 'ramda';

import {AccountingProcessResponseBody} from '@dms/api/sales';

export type AccountingProcessMilestone =
  | 'waiting'
  | 'accepted'
  | 'draft'
  | 'issued'
  | 'documentAvailable'
  | 'failed'
  | 'canceled';

/**
 * Reduces an external accounting process to the one milestone worth showing.
 *
 * Derived from the axes of the endpoint rather than from the notification
 * payload's `status`: the payload carries no version and no sequence number and
 * goes stale by design, and `status` does not exist on the read contract at all.
 *
 * Failure and cancellation win over any progress already made — a printout that
 * arrived before the execution failed is not something to celebrate. Below that,
 * the most advanced axis wins, and anything unrecognised falls through to
 * `waiting` instead of throwing, because the milestone vocabulary is owned by the
 * external accounting system and grows.
 */
export const getAccountingProcessMilestone = (
  accountingProcess: AccountingProcessResponseBody
): AccountingProcessMilestone =>
  match(accountingProcess)
    .with({summaryState: 'failed'}, always<AccountingProcessMilestone>('failed'))
    .with({summaryState: 'canceled'}, always<AccountingProcessMilestone>('canceled'))
    .with({printout: {state: 'available'}}, always<AccountingProcessMilestone>('documentAvailable'))
    .with({document: {state: 'finalized'}}, always<AccountingProcessMilestone>('issued'))
    .with({document: {state: 'draft'}}, always<AccountingProcessMilestone>('draft'))
    .with({request: {accepted: true}}, always<AccountingProcessMilestone>('accepted'))
    .otherwise(always<AccountingProcessMilestone>('waiting'));
